// Shared modal shell — the house dialog chrome, factored out of
// UploadRequestSetModal so every modal (case-issue add/edit, scan, …) gets the
// same backdrop, container, close affordance, and header treatment.
//
//   backdrop : bg-navy/80 + backdrop-blur, click-to-close
//   container: rounded-2xl bg-navy-light border, max-h scroll, top-right ✕
//   header   : amber eyebrow · text-2xl title · gray subtitle
import { useEffect, useRef, type ReactNode } from 'react';
import { XIcon } from 'lucide-react';
import { useFocusTrap } from '../hooks/shared/useFocusTrap';

interface ModalShellProps {
  onClose: () => void;
  children: ReactNode;
  /** Tailwind max-w-* class for the dialog. */
  maxWidth?: string;
  /** When false, backdrop click / Esc / the ✕ are suppressed (blocking op). */
  dismissable?: boolean;
  /** Accessible name for the dialog (when the content has no ModalHeader/<h2>
   *  the AT can key off). Optional — AT otherwise reads the dialog content. */
  ariaLabel?: string;
  /** Priority for a session prompt above already-open dialogs/help sheets. */
  zIndex?: number;
}

export function ModalShell({
  onClose,
  children,
  maxWidth = 'max-w-lg',
  dismissable = true,
  ariaLabel,
  zIndex,
}: ModalShellProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  // Tracks whether the dismissing click was *initiated* on the backdrop
  // rather than starting inside the dialog body (e.g. a text selection that
  // releases outside). Without this, mousedown-inside → drag-out →
  // mouseup-on-backdrop would dismiss the modal because the synthetic
  // `click` fires on the backdrop. Same guard as DocumentCollectionGateModal.
  const backdropMouseDown = useRef(false);
  // Trap focus inside the dialog and restore it on unmount so keyboard / screen
  // reader users can't tab into the background UI behind the modal.
  useFocusTrap(dialogRef, true);

  useEffect(() => {
    if (!dismissable) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, dismissable]);

  return (
    <div
      style={zIndex === undefined ? undefined : {zIndex}}
      className="fixed inset-0 z-50 flex items-center justify-center bg-navy/80 backdrop-blur-sm p-4 sm:p-8 animate-backdrop-in"
      onMouseDown={(e) => {
        backdropMouseDown.current = e.target === e.currentTarget;
      }}
      onClick={(e) => {
        const onBackdrop = e.target === e.currentTarget;
        const startedOnBackdrop = backdropMouseDown.current;
        backdropMouseDown.current = false;
        if (onBackdrop && startedOnBackdrop && dismissable) onClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        onClick={(e) => e.stopPropagation()}
        className={`relative w-full ${maxWidth} max-h-[92vh] overflow-y-auto bg-navy-light border border-navy-lighter rounded-2xl shadow-2xl animate-modal-pop`}
      >
        {dismissable && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="absolute top-4 right-4 p-1.5 rounded-md text-gray-400 hover:text-white hover:bg-navy-lighter transition-colors z-10"
          >
            <XIcon className="w-4 h-4" />
          </button>
        )}
        <div className="px-8 py-10 sm:px-10 sm:py-11">{children}</div>
      </div>
    </div>
  );
}

export function ModalHeader({
  eyebrow,
  title,
  subtitle,
}: {
  eyebrow?: string;
  title: string;
  subtitle?: ReactNode;
}) {
  return (
    <div className="mb-6">
      {eyebrow && (
        <p className="text-[11px] uppercase tracking-wider text-amber/80 font-semibold mb-2">
          {eyebrow}
        </p>
      )}
      <h2 className="text-2xl font-semibold text-white leading-tight">{title}</h2>
      {subtitle && <p className="text-sm text-gray-400 mt-2 leading-relaxed">{subtitle}</p>}
    </div>
  );
}
